import { Fragment, type ReactNode } from 'react';
import { Group, Text, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';

import Markdown from '@/components/content/markdown/Markdown';
import CopyIcon from '@/assets/icons/content/copy.svg';
import {
  SidePanelCard,
  SidePanelCardLabel,
  SidePanelHero,
  SidePanelLayout,
  SidePanelSection,
} from '@/components/content/sidepanel/SidePanelPrimitives';
import RiskIndicators from '../../riskindicators/RiskIndicators';
import IocList from '../../issue/modules/IocList';

import { formatIsoDate } from '@/utils/date';
import { entriesToNormalizedRedmineArray } from '@/utils/normalizeRedmineArrayProp';

import type { FinishedIntelTicket } from '../types';

import sharedClasses from '@/components/content/sidepanel/SidePanel.module.css';
import classes from './FinishedSidePanel.module.css';

type NamedItem = { id: string | number; name: string };

const toItems = (value?: unknown): NamedItem[] =>
  entriesToNormalizedRedmineArray((value ?? {}) as Record<string, string>);

const hasItems = (items: NamedItem[]) => items.length > 0;

const toStringList = (value?: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }

  if (typeof value === 'string') {
    return value
      .split('\r\n')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const KeyValueCard = ({ title, value }: { title: string; value: string }) => (
  <SidePanelCard>
    <SidePanelCardLabel>{title}</SidePanelCardLabel>
    <Text className={sharedClasses.cardValue}>{value}</Text>
  </SidePanelCard>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <SidePanelSection title={title}>{children}</SidePanelSection>
);

const LabeledListCard = ({ title, items }: { title: string; items: string[] }) => {
  const itemOccurrences = new Map<string, number>();
  const keyedItems = items.map((item) => {
    const occurrence = (itemOccurrences.get(item) ?? 0) + 1;
    itemOccurrences.set(item, occurrence);

    return { item, key: `${title}-${item}-${occurrence}` };
  });

  return (
    <SidePanelCard>
      <SidePanelCardLabel>{title}</SidePanelCardLabel>
      <div className={classes.list}>
        {keyedItems.map(({ item, key }) => (
          <Fragment key={key}>
            <Text className={classes.listItem}>{item}</Text>
          </Fragment>
        ))}
      </div>
    </SidePanelCard>
  );
};

const FinishedSidePanel = ({ data }: { data: FinishedIntelTicket }) => {
  const clipboard = useClipboard({ timeout: 1000 });
  const sources = Object.values(data.sources ?? {});
  const locations = toItems(data.locations);
  const industries = toItems(data.industries);
  const technologies = toItems(data.technologies);
  const companies = toItems(data.companies);
  const vulnerabilities = toItems(data.vulnerabilities);
  const threatActors = toItems(data.threat_actors);
  const malwareKits = toItems(data.malware_kits);
  const iocs = toStringList(data.iocs);

  const sourceDetails = [
    data.category ? { title: 'Category', value: data.category } : null,
    data.created_at ? { title: 'Source DTG', value: formatIsoDate(data.created_at, true) } : null,
    sources.length > 0 ? { title: 'Source', value: sources.join(', ') } : null,
    data.url ? { title: 'Reference', value: data.url } : null,
  ].filter((item): item is { title: string; value: string } => item !== null);

  const threatDetails = [
    threatActors.length > 0
      ? { title: 'Threat Actors', values: threatActors.map((item) => item.name) }
      : null,
    data.attack_vector ? { title: 'Attack Vector', values: [data.attack_vector] } : null,
    malwareKits.length > 0
      ? { title: 'Malware Kits', values: malwareKits.map((item) => item.name) }
      : null,
  ].filter(
    (item): item is { title: string; values: string[] } => item !== null && item.values.length > 0
  );

  const contextCards = [
    hasItems(locations)
      ? { title: 'Affected Locations', values: locations.map((item) => item.name) }
      : null,
    hasItems(industries)
      ? { title: 'Industry Sectors', values: industries.map((item) => item.name) }
      : null,
    hasItems(technologies)
      ? { title: 'Technologies', values: technologies.map((item) => item.name) }
      : null,
    hasItems(companies) ? { title: 'Companies', values: companies.map((item) => item.name) } : null,
    hasItems(vulnerabilities)
      ? { title: 'Vulnerabilities', values: vulnerabilities.map((item) => item.name) }
      : null,
  ].filter(
    (item): item is { title: string; values: string[] } => item !== null && item.values.length > 0
  );

  const hasRelevancy = hasItems(locations) || hasItems(industries) || hasItems(technologies);

  return (
    <SidePanelLayout>
      <SidePanelHero title={data.name} subtitle="Finished Intelligence" />

      {hasRelevancy && (
        <Section title="Your Relevancy">
          <SidePanelCard>
            <RiskIndicators
              getValue={() => ({
                locations,
                technologies,
                industry_sectors: industries,
              })}
              id={data.id}
              loading={false}
            />
          </SidePanelCard>
        </Section>
      )}

      {data.description && (
        <Section title="Description">
          <SidePanelCard>
            <Markdown
              text={data.description}
              textProps={{ classNames: { root: classes.description } }}
            />
          </SidePanelCard>
        </Section>
      )}

      {sourceDetails.length > 0 && (
        <Section title="Source Information">
          {sourceDetails.map((detail) => (
            <KeyValueCard key={detail.title} title={detail.title} value={detail.value} />
          ))}
        </Section>
      )}

      {threatDetails.length > 0 && (
        <Section title="Threat Information">
          {threatDetails.map((detail) => (
            <LabeledListCard key={detail.title} title={detail.title} items={detail.values} />
          ))}
        </Section>
      )}

      {contextCards.length > 0 && (
        <Section title="Context">
          {contextCards.map((detail) => (
            <LabeledListCard key={detail.title} title={detail.title} items={detail.values} />
          ))}
        </Section>
      )}

      {iocs.length > 0 && (
        <Section title="Indicators">
          <SidePanelCard>
            <Group justify="space-between" align="center" wrap="nowrap">
              <SidePanelCardLabel>IoC List</SidePanelCardLabel>
              {clipboard.copied ? (
                <Text className={sharedClasses.copyStatus}>Copied</Text>
              ) : (
                <Tooltip label="Copy">
                  <button
                    type="button"
                    aria-label="Copy IoC list"
                    className={sharedClasses.copyButton}
                    onClick={() => clipboard.copy(iocs.join('\n'))}
                  >
                    <CopyIcon />
                  </button>
                </Tooltip>
              )}
            </Group>
            <div className={classes.iocContent}>
              <IocList iocs={iocs} />
            </div>
          </SidePanelCard>
        </Section>
      )}
    </SidePanelLayout>
  );
};

export default FinishedSidePanel;
